import { useEffect, useMemo, useState } from 'react';
import { hole, merke, useMerkState, useRollstelle } from './merkplatz.ts';
import { api, datum, euro, heuteIso, type Anmelder } from '../api.ts';
import { Karte, Leer } from './Geruest.tsx';
import { Dialog, Fehlerkasten, Text, ausFehler, type Fehlerlage } from './Formular.tsx';
import { useFrisch } from './Frisch.tsx';
import { NummernKopieren, Prueffenster } from './Rechnungspruefung.tsx';
import { extern } from './extern.ts';
import { passtSuche } from './Schluesselsuche.tsx';
import { fragen } from './rueckfrage.tsx';

/* DIE FREIGABE DER RECHNUNGEN (.256).
 * ============================================================================
 * ORIGINALTEXT MARVIN (30.09.2026)
 *   „ich will die rechnungen nicht mehrmals anfassen, also will ich sie
 *    prüfen, abgleichen und dann sicher sein, das was im laufwerk ist so in
 *    lexoffice übertragen wird." - „Und dann baue die Freigabe der Rechnungen
 *    im System um. Das kann man noch effektiveren und innovativerer
 *    gestalten."
 *
 * EINE KARTE JE RECHNUNG, EINE PRÜFLISTE JE KARTE. Was die Plattform ohnehin
 * rechnet (Auftrag, Firma, Positionen, Abweichungen zu LEB und Aufmaß,
 * Gutschrift der Telekom, die Datei im Prüfordner), steht als grüne oder
 * rote Marke da. Grün heisst: ein Klick. Alle grünen: ein Klick. Nicht grün:
 * ein Satz, warum trotzdem - oder ablehnen. Erst die Freigabe legt die Datei
 * in den Belege-Ordner, aus dem du sie von Hand in Lexoffice hochlädst. */

const PUNKTNAME: Record<string, string> = {
  auftrag: 'Auftrag', firma: 'Firma', positionen: 'Positionen', befunde: 'LEB und Aufmaß',
  telekom: 'Gutschrift', datei: 'Datei', doppelt: 'Doppelt',
};

type Filter = 'alle' | 'gruen' | 'geprueft' | 'telekom' | 'abweichung' | 'hinweis' | 'zurueck';

export function Rechnungsfreigabe({ ich, suche = '', oeffne }: { ich: Anmelder; suche?: string; oeffne?: (id: string, reiter?: string) => void }) {
  /* ZURÜCK AUS DER AKTE AN DIESELBE STELLE (.479): Daten, Filter, Firma, offenes Prüffenster und Rollstelle
     bleiben gemerkt; frisch geladen wird dahinter. */
  const [d, setD0] = useState<{ karten: any[]; darf_freigeben: boolean; eingeschaltet: boolean } | null>(() => hole('freigabe.daten') ?? null);
  const setD = (n: any) => { merke('freigabe.daten', n); setD0(n); };
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const [hinweis, setHinweis] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState<string | null>(null);
  const [filter, setFilter] = useMerkState<Filter>('freigabe.filter', 'alle');
  const [pruefen, setPruefen] = useMerkState<string | null>('freigabe.pruefen', null);
  const [trotzdem, setTrotzdem] = useState<any | null>(null);
  const [ablehnen, setAblehnen] = useState<any | null>(null);
  const [loeschen, setLoeschen] = useState<any | null>(null);
  async function laden() {
    try { setD(await api.rechnungFreigabe()); setFehler(null); } catch (f) { setFehler(ausFehler(f)); }
  }
  useEffect(() => { laden(); }, []);
  useFrisch(laden);
  /* MEHRFACHAUSWAHL (.393). Marvin, 17.10.2026: „einmal brauche ich hier eine
     Mehrfachselektion für die ganzen Montageaufträge." Haken je Karte,
     „alle sichtbaren wählen", ein Knopf für die Auswahl. Nur grüne gehen
     ohne Nachfrage; eine rote in der Auswahl bleibt liegen und wird genannt. */
  const [wahl, setWahl] = useState<Set<string>>(new Set());
  useRollstelle('abrechnung.schritt2', !!d);

  const karten = d?.karten ?? [];
  /* ZURÜCKGESTELLTE (.340) stehen in ihrem eigenen Filter und nicht unter
     „warten": sie warten auf die Gutschrift, nicht auf einen Menschen. */
  const zurueck = karten.filter((k) => k.ablage === 'pruefung' && k.zurueckgestellt_am);
  const warten = karten.filter((k) => k.ablage === 'pruefung' && !k.zurueckgestellt_am);
  /* NUR WAS GESCHEITERT IST, IST ROT (.454); frisch freigegebene werden im
     Hintergrund verschoben und stehen nur als Zahl da. */
  const haengt = karten.filter((k) => k.ablage === 'freigegeben' && !!k.ablage_fehler);
  const unterwegs = karten.filter((k) => k.ablage === 'freigegeben' && !k.ablage_fehler).length;
  const gruen = warten.filter((k) => k.gruen);
  const telekom = warten.filter((k) => k.punkte.some((p: any) => p.schluessel === 'telekom' && !p.ok));
  /* GEPRÜFT UND NICHT GRÜN (.312): wartet nur noch auf die Gutschrift. */
  const geprueft = warten.filter((k) => k.geprueft && !k.gruen);
  /* .480: was ohne weiteren Satz freigeht: grün, oder geprüft und nur die Gutschrift fehlt. */
  const bereit = warten.filter((k) => k.gruen || k.freigebbar);
  /* .572: NUR DIE GUTSCHRIFT FEHLT, sonst ist alles in Ordnung. Marvin, 01.11.2026:
     „da gibt es keine Gutschrift, deswegen muss ich mir trotzdem irgendwie die
     Rechnung quittieren können." Solche Rechnungen nimmt die Auswahl nach einer
     Rückfrage mit (Grund „ohne Gutschrift freigegeben"). */
  const nurGutschriftFehlt = (k: any) => !k.gruen && !k.freigebbar && !(k.punkte ?? []).some((p: any) => !p.ok && p.stufe !== 'hinweis' && p.schluessel !== 'telekom')
    && (k.punkte ?? []).some((p: any) => p.schluessel === 'telekom' && !p.ok);
  const geht = (k: any) => k.gruen || k.freigebbar || nurGutschriftFehlt(k);
  const abweichung = warten.filter((k) => k.punkte.some((p: any) => p.schluessel === 'befunde' && !p.ok));
  /* MIT HINWEIS (.365): orange, hält nichts auf, will aber angesehen werden. */
  const mitHinweis = warten.filter((k) => k.punkte.some((p: any) => p.stufe === 'hinweis'));
  /* DIE SUCHE DER SEITE (.300): KLS-ID, SM, Anschrift, Firma, Nummer. */
  const passt = (k: any) => passtSuche(suche, k.firma, k.rechnungsnummer,
    ...(k.auftraege ?? []).flatMap((a: any) => [a.kls_id, a.sm_nummer, a.anschrift]));
  /* NACH FIRMA (.414). Marvin, 19.10.2026: „Außerdem fehlt mir hier immer noch
     die Filterung bei den Rechnungen nach der Firma. Und natürlich auch die
     Anzeige, wie viel da drin stehen." Chips je Firma unter den Kacheln, mit
     der Zahl im gewählten Kachelfilter; der Firmenchip grenzt die Karten ein. */
  const [firma, setFirma] = useMerkState<string | null>('freigabe.firma', null);
  const imFilter = useMemo(() => (filter === 'gruen' ? gruen : filter === 'geprueft' ? geprueft : filter === 'telekom' ? telekom : filter === 'abweichung' ? abweichung : filter === 'hinweis' ? mitHinweis : filter === 'zurueck' ? zurueck : warten)
    .filter(passt), [filter, d, suche]);
  const firmen = useMemo(() => [...new Set(karten.map((k) => String(k.firma ?? 'ohne Firma')))].sort((a, b) => a.localeCompare(b, 'de')), [d]);
  const zaehlFirma = (f: string) => imFilter.filter((k) => String(k.firma ?? 'ohne Firma') === f).length;
  const sicht = useMemo(() => imFilter.filter((k) => !firma || String(k.firma ?? 'ohne Firma') === firma), [imFilter, firma]);

  async function tu(schluessel: string, f: () => Promise<string | null>) {
    setLaeuft(schluessel); setFehler(null); setHinweis(null);
    try { const t = await f(); if (t) setHinweis(t); await laden(); }
    catch (x) { setFehler(ausFehler(x)); } finally { setLaeuft(null); }
  }
  const nachFreigabe = (r: { abgelegt: boolean; fehler: string | null; im_hintergrund?: boolean }) => r.abgelegt
    ? 'Freigegeben und in den Belege-Ordner gelegt.'
    : r.im_hintergrund ? 'Freigegeben. Die Datei wird im Hintergrund in den Belege-Ordner verschoben.'
    : `Freigegeben. Die Datei liegt noch im Prüfordner (${r.fehler ?? 'Grund unbekannt'}); der Lauf versucht es jede Stunde.`;

  if (d && !d.eingeschaltet && !karten.length) return null;

  return (
    <>
      <Karte titel={`Zur Freigabe (${warten.length})`}
             rechts={d?.darf_freigeben && bereit.length > 0 ? (
               <button className="knopf klein haupt" disabled={!!laeuft}
                       onClick={() => tu('alle', async () => {
                         const r = await api.rechnungenFreigeben(bereit.map((k) => k.id));
                         return `${r.freigegeben} freigegeben. Die Dateien werden im Hintergrund in den Belege-Ordner verschoben.`;
                       })}>
                 {laeuft === 'alle' ? 'Einen Moment' : `Alle grünen und geprüften freigeben (${bereit.length})`}
               </button>
             ) : undefined}>
        <Fehlerkasten lage={fehler} />
        {hinweis && <div className="hinweis">✅ {hinweis}</div>}
        {d && !d.eingeschaltet && (
          <p className="klein leise">Die Freigabe vor der Ablage ist ausgeschaltet (Konfiguration, „Rechnungen zu Telekom-Aufträgen erst prüfen"). Neue Rechnungen gehen sofort in den Ordner.</p>
        )}
        <p className="klein leise" style={{ marginTop: 0 }}>
          Rechnungen zu Telekom-Aufträgen liegen bis zur Freigabe im Prüfordner der QNAP. Erst freigegeben kommen sie in
          den Belege-Ordner, aus dem du sie von Hand in Lexoffice hochlädst. Alle anderen Rechnungen gehen wie bisher sofort dorthin.
        </p>

        <div className="freigabekacheln mitgeprueft">
          {([['alle', 'warten', warten.length, ''], ['gruen', 'alles grün', gruen.length, 'gruen'],
             ['geprueft', 'geprüft, wartet', geprueft.length, 'warnung'],
             ['telekom', 'Gutschrift fehlt', telekom.length, 'rot'], ['abweichung', 'mit Abweichung', abweichung.length, 'rot'],
             ['hinweis', 'mit Hinweis', mitHinweis.length, 'orange'],
             ['zurueck', 'zurückgestellt', zurueck.length, '']] as const)
            .map(([f, t, n, farbe]) => (
              <button key={f} type="button" aria-pressed={filter === f}
                      className={`freigabekachel ${farbe} ${filter === f ? 'an' : ''}`} onClick={() => setFilter(f)}>
                <span className="zahl">{n}</span><span>{t}</span>
              </button>
            ))}
        </div>

        {firmen.length > 1 && (
          <div className="ga-filter" style={{ marginBottom: 10 }}>
            {firmen.map((f) => (
              <button key={f} type="button" aria-pressed={firma === f} className={`ga-chip ${firma === f ? 'an' : ''}`}
                      onClick={() => setFirma(firma === f ? null : f)}>{f} {zaehlFirma(f)}</button>
            ))}
          </div>
        )}

        {unterwegs > 0 && <p className="klein leise" style={{ margin: '0 0 8px' }}>{unterwegs} freigegeben, {unterwegs === 1 ? 'die Datei wird' : 'die Dateien werden'} gerade im Hintergrund verschoben.</p>}
        {haengt.length > 0 && (
          <div className="hinweis fehler" style={{ marginBottom: 12 }}>
            <b>Freigegeben, aber das Verschieben ist gescheitert ({haengt.length}).</b> Der Lauf versucht es jede Stunde.
            {haengt.map((k) => (
              <div key={k.id} className="reihe" style={{ gap: 8, alignItems: 'center', marginTop: 6 }}>
                <span>{k.firma ?? 'Firma?'}, {k.rechnungsnummer ?? 'ohne Nummer'}: {k.ablage_fehler ?? ''}</span>
                {d?.darf_freigeben && (
                  <button className="knopf klein" disabled={!!laeuft}
                          onClick={() => tu(k.id, async () => {
                            const r = await api.rechnungAblegen(k.id);
                            return r.ok ? 'Abgelegt.' : `Noch nicht: ${r.fehler ?? ''}`;
                          })}>noch einmal ablegen</button>
                )}
              </div>
            ))}
          </div>
        )}

        {d && sicht.length > 0 && d.darf_freigeben && (
          <div className="reihe" style={{ gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
            <label className="klein" style={{ display: 'flex', gap: 6, alignItems: 'center', cursor: 'pointer' }}>
              <input type="checkbox" checked={sicht.every((k) => wahl.has(k.id))}
                     onChange={() => { const alle = sicht.every((k) => wahl.has(k.id)); const n = new Set(wahl); for (const k of sicht) { if (alle) n.delete(k.id); else n.add(k.id); } setWahl(n); }} />
              alle {sicht.length} sichtbaren wählen
            </label>
            <button className="knopf haupt klein" disabled={!!laeuft || sicht.filter((k) => wahl.has(k.id) && geht(k)).length === 0}
                    onClick={async () => {
                      const sofort = sicht.filter((k) => wahl.has(k.id) && (k.gruen || k.freigebbar));
                      const ohneGs = sicht.filter((k) => wahl.has(k.id) && nurGutschriftFehlt(k));
                      const rot = sicht.filter((k) => wahl.has(k.id) && !geht(k)).length;
                      if (ohneGs.length && !await fragen(`${ohneGs.length} ${ohneGs.length === 1 ? 'Rechnung hat' : 'Rechnungen haben'} keine Gutschrift der Telekom, sonst ist alles in Ordnung:\n\n${ohneGs.map((k) => `${k.rechnungsnummer ?? 'ohne Nummer'}, ${(k.auftraege ?? [])[0]?.anschrift ?? ''}`).join('\n')}\n\nOhne Gutschrift mit freigeben?`, { titel: 'Ohne Gutschrift freigeben', ja: 'Ja, mit freigeben', nein: 'Nein, nur die grünen' })) {
                        if (!sofort.length) return;
                        ohneGs.length = 0;
                      }
                      await tu('auswahl', async () => {
                        let n = 0;
                        if (sofort.length) n += (await api.rechnungenFreigeben(sofort.map((k) => k.id))).freigegeben;
                        for (const k of ohneGs) { await api.rechnungFreigeben(k.id, 'Ohne Gutschrift freigegeben (Auswahl)'); n++; }
                        setWahl(new Set());
                        return `${n} freigegeben. Die Dateien werden im Hintergrund in den Belege-Ordner verschoben.`
                          + (rot ? ` ${rot} ${rot === 1 ? 'gewählte Rechnung hat' : 'gewählte Rechnungen haben'} noch eine Abweichung und ${rot === 1 ? 'bleibt' : 'bleiben'} zur Prüfung stehen.` : '');
                      });
                    }}>
              Ausgewählte freigeben ({sicht.filter((k) => wahl.has(k.id) && geht(k)).length})
            </button>
            {sicht.some((k) => wahl.has(k.id) && nurGutschriftFehlt(k)) && <span className="klein leise">{sicht.filter((k) => wahl.has(k.id) && nurGutschriftFehlt(k)).length} ohne Gutschrift: wird vorher gefragt.</span>}
            {sicht.some((k) => wahl.has(k.id) && !geht(k)) && <span className="klein leise">{sicht.filter((k) => wahl.has(k.id) && !geht(k)).length} der Auswahl mit Abweichung: die bleiben stehen.</span>}
          </div>
        )}
        {d && !sicht.length && (
          <Leer text={suche.trim() && warten.length ? 'Keine wartende Rechnung passt zur Suche. Freigegebene und bezahlte stehen unten bei den Rechnungen der Subunternehmer.' : warten.length ? 'In diesem Filter ist nichts.' : 'Keine Rechnung wartet auf Freigabe.'} />
        )}
        <div className="freigabekarten">
          {sicht.map((k) => {
            const ueber = k.faellig_am && k.faellig_am < heuteIso();
            return (
              <div key={k.id} className={`freigabekarte ${k.gruen ? 'gruen' : k.geprueft ? 'geprueft' : 'offen'}`}>
                <div className="freigabekopf">
                  <div className="reihe" style={{ gap: 8, alignItems: 'flex-start' }}>
                    {d?.darf_freigeben && (
                      <input type="checkbox" aria-label="auswählen" checked={wahl.has(k.id)} style={{ marginTop: 4 }}
                             onChange={() => { const n = new Set(wahl); if (n.has(k.id)) n.delete(k.id); else n.add(k.id); setWahl(n); }} />
                    )}
                    <div>
                      <div className="freigabefirma">{k.firma ?? 'Firma nicht erkannt'}</div>
                      <div className="klein leise">
                        {k.rechnungsnummer ? `Nr. ${k.rechnungsnummer}` : 'ohne Nummer'}
                        {k.rechnungsdatum ? `, vom ${datum(k.rechnungsdatum)}` : ''}
                      </div>
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    {k.betrag !== undefined && k.betrag !== null && <div className="freigabebetrag">{euro(k.betrag)}</div>}
                    {k.geprueft && <div className="merker blau" title={k.entscheidung ?? ''}>geprüft{k.geprueft_am ? ` am ${datum(k.geprueft_am)}` : ''}</div>}
                    {k.faellig_am && (
                      <div className={ueber ? 'merker fehler' : 'klein leise'}>fällig {datum(k.faellig_am)}</div>
                    )}
                  </div>
                </div>
                {k.geprueft && k.entscheidung && <div className="klein leise" style={{ marginBottom: 4 }}>Vermerk: {k.entscheidung}</div>}
                {k.zurueckgestellt_am && (
                  <div className="merker" style={{ marginBottom: 4 }}>
                    zurückgestellt am {datum(k.zurueckgestellt_am)}{k.zurueckgestellt_grund ? `: ${k.zurueckgestellt_grund}` : ''}, wird vorgeholt, sobald der Auftrag freigegeben ist
                  </div>
                )}
                {/* ZUM AUFTRAG SPRINGEN, KLS UND SM KOPIEREN (.393). Marvin,
                    17.10.2026: „dass ich dann hier den KLS oder SM markieren kann,
                    beziehungsweise dann auf den Auftrag springen kann, um zu
                    gucken, was ist da beim Auftrag Sache." */}
                {k.auftraege.map((a: any) => (
                  <div key={a.id} className="klein reihe" style={{ gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    {oeffne
                      ? <button type="button" className="alslink" onClick={() => oeffne(a.id, 'leb')} title="Auftragsakte öffnen"><b>{a.anschrift || 'ohne Anschrift'}</b></button>
                      : <span>{a.anschrift || 'ohne Anschrift'}</span>}
                    <NummernKopieren kls={a.kls_id} sm={a.sm_nummer} />
                  </div>
                ))}
                <ul className="pruefliste">
                  {/* ROT UND ORANGE SIND ANKLICKBAR (.365). Marvin, 14.10.2026:
                      „Wenn Abweichungen sind, lassen wir es auch rot. Muss aber
                      anklickbar sein ... Duplikate anklickbar anzeigen lassen."
                      Jeder Punkt, der nicht grün ist, öffnet das Prüffenster:
                      die Dubletten stehen dort oben, die Gegenprüfung und der
                      Positionsvergleich darunter. */}
                  {k.punkte.map((p: any) => {
                    const stufe = p.stufe ?? (p.ok ? 'ok' : 'nein');
                    const zeichen = stufe === 'ok' ? '✓' : stufe === 'wartet' ? '…' : stufe === 'hinweis' ? '?' : '!';
                    const klick = stufe !== 'ok';
                    return (
                      <li key={p.schluessel + p.text} className={stufe} title={PUNKTNAME[p.schluessel]}>
                        <span className="zeichen" aria-hidden>{zeichen}</span>
                        {/* DIE PDF FEHLT IM PRÜFORDNER (.444): die Plattform legt sie
                            selbst dorthin, ein Klick, danach ist der Punkt grün. */}
                        {p.schluessel === 'datei' && !p.ok && /noch nicht im Prüfordner/.test(p.text)
                          ? <span><b>{PUNKTNAME[p.schluessel]}:</b> {p.text}{' '}
                              <button type="button" className="knopf klein" disabled={laeuft === 'ordner' + k.id} onClick={() => tu('ordner' + k.id, async () => (await api.rechnungInDenPruefordner(k.id)).klartext ?? null)}>in den Prüfordner legen</button>
                            </span>
                          : klick
                          ? <button type="button" className="pruefpunkt" onClick={() => setPruefen(k.id)}
                                    title={p.schluessel === 'doppelt' ? 'Beide Rechnungen nebeneinander ansehen' : 'Im Prüffenster ansehen'}>
                              <b>{PUNKTNAME[p.schluessel]}:</b> {p.text}
                            </button>
                          : <span><b>{PUNKTNAME[p.schluessel]}:</b> {p.text}</span>}
                      </li>
                    );
                  })}
                </ul>
                <div className="freigabefuss">
                  {/* OHNE DATEI IM PRÜFORDNER GIBT ES NICHTS ZU VERSCHIEBEN: dann
                      auch keinen Knopf, der am Server nur abgewiesen würde. */}
                  {d?.darf_freigeben && !(k.punkte ?? []).some((p: any) => p.schluessel === 'datei' && !p.ok) && (
                    <button className={k.gruen || k.freigebbar ? 'knopf haupt' : 'knopf'} disabled={!!laeuft}
                            onClick={() => k.gruen || k.freigebbar
                              ? tu(k.id, async () => nachFreigabe(await api.rechnungFreigeben(k.id)))
                              : setTrotzdem(k)}>
                      {laeuft === k.id ? 'Einen Moment' : k.gruen ? 'Freigeben' : k.freigebbar ? 'Freigeben (geprüft)' : 'Trotzdem freigeben …'}
                    </button>
                  )}
                  <button className="knopf" onClick={() => setPruefen(k.id)}>Prüfen</button>
                  <button className="knopf" onClick={() => extern(api.rechnungAdresse(k.id))}>PDF</button>
                  {d?.darf_freigeben && (
                    <button className="knopf" disabled={!!laeuft} onClick={() => setAblehnen(k)}>Ablehnen …</button>
                  )}
                  {/* ZURÜCKSTELLEN UND LÖSCHEN (.340). Marvin, 10.10.2026: „die
                      Möglichkeit haben, Rechnungen raus zu löschen, weil sie zu
                      einem späteren Zeitpunkt nochmal gesendet wird ... oder sie
                      bleiben so lange liegen bis ich es eingereicht habe." */}
                  {k.zurueckgestellt_am
                    ? <button className="knopf" disabled={!!laeuft}
                              onClick={() => tu(k.id, async () => { await api.rechnungVorholen(k.id); return 'Vorgeholt.'; })}>Vorholen</button>
                    : <button className="knopf" disabled={!!laeuft} title="Bleibt liegen, bis der Auftrag zur Abrechnung freigegeben ist"
                              onClick={() => tu(k.id, async () => (await api.rechnungZurueckstellen(k.id, null)).klartext)}>Zurückstellen</button>}
                  {d?.darf_freigeben && (
                    <button className="knopf" disabled={!!laeuft} title="Ohne Sperre: kommt die Rechnung noch einmal, wird sie wieder angelegt"
                            onClick={() => setLoeschen(k)}>Löschen …</button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </Karte>

      {pruefen && <Prueffenster ich={ich} id={pruefen} zu={() => setPruefen(null)} fertig={() => { setPruefen(null); laden(); }} />}
      {trotzdem && (
        <Begruendung titel="Trotzdem freigeben" knopf="Freigeben"
          text={`Nicht grün: ${trotzdem.punkte.filter((p: any) => !p.ok).map((p: any) => `${PUNKTNAME[p.schluessel]} (${p.text})`).join(', ')}. Warum trotzdem?`}
          zu={() => setTrotzdem(null)}
          los={async (g) => { const k = trotzdem; setTrotzdem(null); await tu(k.id, async () => nachFreigabe(await api.rechnungFreigeben(k.id, g))); }} />
      )}
      {loeschen && (
        <Begruendung titel="Rechnung löschen" knopf="Löschen"
          text={`Rechnung ${loeschen.rechnungsnummer ?? 'ohne Nummer'} von ${loeschen.firma ?? 'Firma?'} wird aus der Plattform genommen, ohne Sperre: schickt die Firma sie später noch einmal, wird sie wieder angelegt. Die Datei auf der QNAP bleibt. Warum?`}
          zu={() => setLoeschen(null)}
          los={async (g) => { const k = loeschen; setLoeschen(null); await tu(k.id, async () => (await api.rechnungGanzLoeschen(k.id, g)).klartext); }} />
      )}
      {ablehnen && (
        <Begruendung titel="Rechnung ablehnen" knopf="Ablehnen"
          text="Die Datei kommt in den Ordner „Abgelehnt“ und wird nicht gelöscht. Warum?"
          zu={() => setAblehnen(null)}
          los={async (g) => { const k = ablehnen; setAblehnen(null); await tu(k.id, async () => {
            const r = await api.rechnungAblehnen(k.id, g);
            return r.abgelegt ? 'Abgelehnt, die Datei liegt im Ordner „Abgelehnt“.' : `Abgelehnt. Die Datei liegt noch im Prüfordner (${r.fehler ?? ''}).`;
          }); }} />
      )}
    </>
  );
}

function Begruendung({ titel, text, knopf, zu, los }: {
  titel: string; text: string; knopf: string; zu: () => void; los: (grund: string) => Promise<void>;
}) {
  const [g, setG] = useState('');
  return (
    <Dialog titel={titel} breite={560} zu={zu}>
      <form onSubmit={(e) => { e.preventDefault(); if (g.trim().length >= 5) los(g.trim()); }}>
        <p style={{ marginTop: 0 }}>{text}</p>
        <Text name="grund" titel="Begründung" mehrzeilig wert={g} setze={setG} hoechstens={1000} />
        <div className="reihe" style={{ gap: 8, marginTop: 12 }}>
          <button className="knopf haupt" disabled={g.trim().length < 5}>{knopf}</button>
          <button type="button" className="knopf" onClick={zu}>Abbrechen</button>
        </div>
      </form>
    </Dialog>
  );
}
